/**
 * The AVA Assistant's per-person settings (showcase: src/assets/showcase/ai-chat-client-settings-showcase.html).
 * Appearance and behaviour are per-viewer conveniences kept in this browser; the tool groups the user
 * turned off also travel with every message (`client.toolGroupsOff`) so the server hides those tools.
 * Every function is immutable. No Vue or path-alias imports, so node --test can load it directly.
 */

export const THEME = Object.freeze({ AVA: 'ava', LIGHT: 'light', DARK: 'dark' });
/** Text size: − / + in 10% steps (Khalid 2026-10-13); past 150% the side panel's composer and steps crowd. */
export const TEXT_SCALE = Object.freeze({ min: 0.8, max: 1.5, step: 0.1, standard: 1 });

/** Rounds to a 10% step without float drift (1.1 + 0.1 is 1.2, not 1.2000000000000002). */
const toStep = (v) => Math.round(v / TEXT_SCALE.step + 1e-9) / Math.round(1 / TEXT_SCALE.step);
const inRange = (v) => typeof v === 'number' && Number.isFinite(v) && v >= TEXT_SCALE.min - 1e-9 && v <= TEXT_SCALE.max + 1e-9;

/** One − (-1) or + (+1) press, kept inside the range. */
export function stepTextScale(current, direction) {
  const next = toStep((inRange(current) ? current : TEXT_SCALE.standard) + Math.sign(direction) * TEXT_SCALE.step);
  return Math.min(TEXT_SCALE.max, Math.max(TEXT_SCALE.min, next));
}

export const SEND_KEY = Object.freeze({ ENTER: 'enter', CTRL: 'ctrl' });

/** What the AVA connector may use, in business words. The server maps each tool to one group. */
export const TOOL_GROUPS = Object.freeze([
  { key: 'facilities', icon: 'globe-alt', title: 'Facilities and companies', note: 'Find and open the facilities you can access' },
  { key: 'dashboards', icon: 'squares-2x2', title: 'Explorer', note: 'Read and build dashboards and charts' },
  { key: 'data', icon: 'chart-bar', title: 'Data sources', note: 'Read registers and combined sources' },
  { key: 'tags', icon: 'tag', title: 'Tags and findings', note: 'Tags, anomalies, inspections and their history' },
  { key: 'tasks', icon: 'clipboard-document', title: 'Tasks and boards', note: 'Read tasks; create or change them when you approve' },
]);

const THEMES = Object.values(THEME);
const SEND_KEYS = Object.values(SEND_KEY);
const GROUP_KEYS = TOOL_GROUPS.map((g) => g.key);

export const DEFAULT_PREFERENCES = Object.freeze({
  theme: THEME.AVA,
  textScale: TEXT_SCALE.standard,
  sendKey: SEND_KEY.ENTER,
  stepsOpen: false,
  showToolNames: false,
  toolGroups: Object.freeze(Object.fromEntries(GROUP_KEYS.map((k) => [k, true]))),
});

const VALID = Object.freeze({
  theme: (v) => THEMES.includes(v),
  textScale: inRange,
  sendKey: (v) => SEND_KEYS.includes(v),
  stepsOpen: (v) => typeof v === 'boolean',
  showToolNames: (v) => typeof v === 'boolean',
  toolGroups: (v) => !!v && typeof v === 'object',
});

const groupsFrom = (raw) => Object.fromEntries(GROUP_KEYS.map((k) => [k, raw && raw[k] === false ? false : true]));

/** Stored or incoming settings, each value checked; anything unknown or invalid is the default. */
export function normalizePreferences(raw) {
  const source = raw && typeof raw === 'object' ? raw : {};
  const out = {};
  for (const key of Object.keys(DEFAULT_PREFERENCES)) {
    const value = source[key];
    if (key === 'toolGroups') out.toolGroups = groupsFrom(VALID.toolGroups(value) ? value : null);
    else if (key === 'textScale') out.textScale = inRange(value) ? toStep(value) : TEXT_SCALE.standard;
    else out[key] = VALID[key](value) ? value : DEFAULT_PREFERENCES[key];
  }
  return out;
}

/** One setting changed; an invalid value changes nothing. */
export function withPreference(prefs, key, value) {
  if (!VALID[key] || !VALID[key](value)) return prefs;
  return normalizePreferences({ ...prefs, [key]: value });
}

/** Tool groups the user turned off, sent to the server so it doesn't offer those tools. */
export const toolGroupsOff = (prefs) => GROUP_KEYS.filter((k) => prefs.toolGroups && prefs.toolGroups[k] === false);

/** 'light' or 'dark' for the assistant: the user's pick, or AVA's own theme. */
export function resolveTheme(prefs, avaIsDark) {
  if (prefs.theme === THEME.LIGHT || prefs.theme === THEME.DARK) return prefs.theme;
  return avaIsDark ? 'dark' : 'light';
}

/** Whether this keydown sends the message, given the "Send with" setting. Shift+Enter is always a new line. */
export function sendsMessage(event, sendKey) {
  if (!event || event.key !== 'Enter' || event.shiftKey) return false;
  const withModifier = !!(event.ctrlKey || event.metaKey);
  return sendKey === SEND_KEY.CTRL ? withModifier : true;
}
